import { ZapIcon } from '@persona-id/design-tokens/icons';
import BookOpenIcon from '@persona-id/design-tokens/icons/BookOpenIcon';
import BriefcaseIcon from '@persona-id/design-tokens/icons/BriefcaseIcon';
import CircleCheckIcon from '@persona-id/design-tokens/icons/CircleCheckIcon';
import CodeIcon from '@persona-id/design-tokens/icons/CodeIcon';
import DatabaseIcon from '@persona-id/design-tokens/icons/DatabaseIcon';
import DocumentIcon from '@persona-id/design-tokens/icons/DocumentIcon';
import GlobeIcon from '@persona-id/design-tokens/icons/GlobeIcon';
import GraphIcon from '@persona-id/design-tokens/icons/GraphIcon';
import GridIcon from '@persona-id/design-tokens/icons/GridIcon';
import HomeIcon from '@persona-id/design-tokens/icons/HomeIcon';
import IdFrontIcon from '@persona-id/design-tokens/icons/IdFrontIcon';
import LinkExternalIcon from '@persona-id/design-tokens/icons/LinkExternalIcon';
import ListIcon from '@persona-id/design-tokens/icons/ListIcon';
import MergeIcon from '@persona-id/design-tokens/icons/MergeIcon';
import UsersCheckIcon from '@persona-id/design-tokens/icons/UsersCheckIcon';
import UsersIcon from '@persona-id/design-tokens/icons/UsersIcon';
import WebhooksIcon from '@persona-id/design-tokens/icons/WebhooksIcon';
import WorkflowsIcon from '@persona-id/design-tokens/icons/WorkflowsIcon';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { NavbarItemData } from 'dashboard/components/Navbar/NavbarItem';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Environment } from 'dashboard/models/environment';
import {
  Organization,
  OrganizationStatus,
  ProductFeatureName,
} from 'dashboard/models/organization';
import { OrganizationPlan, OrganizationPlanKey } from 'dashboard/models/organization-plan';
import { NavItem } from 'dashboard/models/role';
import { RootState } from 'dashboard/redux/store';
import { LATEST_API_VERSION } from 'lib/constants/api';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

export interface SectionData {
  id: string;
  label: string;
  items: NavbarItemData[];
}

const useSidebarConfig = ({
  readmeJwtToken,
}: {
  readmeJwtToken: string | undefined;
}): SectionData[] => {
  const organization = useSelector<RootState, Organization>(
    (state) => state.organization.organization,
  );

  const currentEnvironment = useSelector<RootState, Environment>(
    (state) => state.currentEnvironment.currentEnvironment,
  );

  const currentOrganizationPlan = useSelector<RootState, OrganizationPlan>(
    (state) => state.organization.currentOrganizationPlan,
  );

  // We intentionally do not show the billing tab to Restricted Orgs b/c this is a plan commonly applied to competitors
  // or other malicious users that we don't want to see our pricing
  const disableBillingTab =
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Restricted;

  const homeData: NavbarItemData = {
    id: 'home',
    label: 'Home',
    navItem: NavItem.HomeHeader,
    navItemRedesign: NavItem.HomeHeader,
    prefixIcon: <HomeIcon />,
    links: [
      {
        id: 'home',
        label: 'Home',
        route: '/dashboard/home',
        navItem: NavItem.Home,
        navItemRedesign: NavItem.Home,
      },
    ],
  };

  const inquiryData: NavbarItemData = {
    id: 'inquiries',
    label: 'Inquiries',
    navItem: NavItem.InquiryHeader,
    navItemRedesign: NavItem.InquiryHeader,
    prefixIcon: <IdFrontIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'inquiries-all',
        label: 'All Inquiries',
        route: '/dashboard/inquiries',
        navItem: NavItem.Inquiry,
        navItemRedesign: NavItem.Inquiry,
      },
      {
        id: 'inquiries-analytics',
        label: 'Analytics',
        route: '/dashboard/inquiry-analytics',
        navItem: NavItem.InquiryAnalytics,
        navItemRedesign: NavItem.InquiryAnalytics,
      },
      {
        id: 'inquiries-templates',
        label: 'Templates',
        route: '/dashboard/inquiry-templates',
        navItem: NavItem.InquiryTemplate,
        navItemRedesign: NavItem.InquiryTemplate,
      },
      {
        // 2.0 theme editor
        id: 'inquiries-theme',
        label: 'Themes (Legacy)',
        route: '/dashboard/editor',
        navItem: NavItem.InquiryThemeEditor,
        navItemRedesign: NavItem.InquiryThemeEditor,
        hidden: productFeatureDisabled(
          organization,
          ProductFeatureName.LegacyInquiryTemplates,
          currentEnvironment?.type,
        ),
      },
      {
        // theme set
        id: 'inquiries-theme-set',
        label: 'Themes',
        route: '/dashboard/theme-sets',
        navItem: NavItem.InquiryThemeSet,
        navItemRedesign: NavItem.InquiryThemeSet,
        hidden: !useFeatureFlag('dashboard:fle-theme-set-index-and-editor'),
      },
      {
        id: 'inquiry-manage-domain',
        label: 'Domain Manager',
        route: '/dashboard/inquiry-manage-domain',
        navItem: NavItem.InquiryDomainManager,
        navItemRedesign: NavItem.InquiryDomainManager,
      },
    ],
  };

  const verificationData: NavbarItemData = {
    id: 'verifications',
    label: 'Verifications',
    navItem: NavItem.VerificationHeader,
    navItemRedesign: NavItem.VerificationHeader,
    prefixIcon: <CircleCheckIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'verifications-all',
        label: 'All Verifications',
        route: '/dashboard/verifications',
        navItem: NavItem.Verification,
        navItemRedesign: NavItem.Verification,
      },
      {
        id: 'verifications-analytics',
        label: 'Analytics',
        route: '/dashboard/verification-analytics',
        navItem: NavItem.VerificationAnalytics,
        navItemRedesign: NavItem.VerificationAnalytics,
      },
      {
        id: 'verifications-templates',
        label: 'Templates',
        route: '/dashboard/verification-templates',
        navItem: NavItem.VerificationTemplate,
        navItemRedesign: NavItem.VerificationTemplate,
      },
    ],
  };

  const accountData: NavbarItemData = {
    id: 'accounts',
    label: 'Accounts',
    navItem: NavItem.AccountHeader,
    navItemRedesign: NavItem.AccountHeader,
    prefixIcon: <UsersCheckIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'accounts-all',
        label: 'All Accounts',
        route: '/dashboard/accounts',
        navItem: NavItem.Account,
        navItemRedesign: NavItem.Account,
      },
      {
        id: 'account-types',
        label: 'Types',
        route: '/dashboard/account-types',
        navItem: NavItem.Account,
        navItemRedesign: NavItem.AccountTypes,
        hidden: !useFeatureFlag('dashboard:accounts-show-account-type'),
      },
    ],
  };

  const transactionData: NavbarItemData = {
    id: 'transactions',
    label: 'Transactions',
    navItem: NavItem.TransactionHeader,
    navItemRedesign: NavItem.TransactionHeader,
    prefixIcon: <MergeIcon />,
    hidden: productFeatureDisabled(
      organization,
      ProductFeatureName.TransactionsDashboard,
      currentEnvironment?.type,
    ),
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'transactions-all',
        label: 'All Transactions',
        route: '/dashboard/transactions',
        navItem: NavItem.Transactions,
        navItemRedesign: NavItem.Transactions,
      },
      {
        id: 'transaction-analytics',
        label: 'Analytics',
        route: '/dashboard/transaction-analytics/volume',
        navItem: NavItem.Transactions,
        navItemRedesign: NavItem.TransactionAnalytics,
      },
      {
        id: 'transaction-types',
        label: 'Types',
        route: '/dashboard/transaction-types',
        navItem: NavItem.Transactions,
        navItemRedesign: NavItem.TransactionTypes,
      },
    ],
  };

  const reportData: NavbarItemData = {
    id: 'reports',
    label: 'Reports',
    navItem: NavItem.ReportHeader,
    navItemRedesign: NavItem.ReportHeader,
    prefixIcon: <DocumentIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'reports-all',
        label: 'All Reports',
        route: '/dashboard/reports',
        navItem: NavItem.Report,
        navItemRedesign: NavItem.Report,
      },
      {
        id: 'report-templates',
        label: 'Templates',
        route: '/dashboard/report-templates',
        navItem: NavItem.ReportTemplate,
        navItemRedesign: NavItem.ReportTemplate,
      },
      {
        id: 'report-analytics',
        label: 'Analytics',
        route: '/dashboard/reports-analytics',
        navItem: NavItem.Report,
        navItemRedesign: NavItem.ReportAnalytics,
        disabled: !usePermission(Permission.ReportView),
      },
    ],
  };

  const graphV2Data: NavbarItemData = {
    id: 'graph-v2',
    label: 'Graph',
    navItem: NavItem.GraphHeader,
    navItemRedesign: NavItem.GraphHeader,
    prefixIcon: <GraphIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'graph-explorer',
        label: 'Explorer',
        route: '/dashboard/explorer',
        navItem: NavItem.Graph,
        navItemRedesign: NavItem.Graph,
      },
      {
        id: 'graph-templates',
        label: 'Templates',
        route: '/dashboard/graph-query-templates',
        navItem: NavItem.Graph,
        navItemRedesign: NavItem.GraphTemplates,
      },
      {
        id: 'graph-v2-config',
        label: 'Configuration',
        route: '/dashboard/graph-configuration',
        navItem: NavItem.Graph,
        navItemRedesign: NavItem.GraphConfig,
      },
    ],
  };

  const caseData: NavbarItemData = {
    id: 'cases',
    label: 'Cases',
    navItem: NavItem.CaseHeader,
    navItemRedesign: NavItem.CaseHeader,
    prefixIcon: <BriefcaseIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'cases-all',
        label: 'All Cases',
        route: '/dashboard/cases',
        navItem: NavItem.Case,
        navItemRedesign: NavItem.Case,
      },
      {
        id: 'case-queues',
        label: 'Queues',
        route: '/dashboard/case-queues',
        navItem: NavItem.CaseQueues,
        navItemRedesign: NavItem.CaseQueues,
      },
      {
        id: 'case-analytics',
        label: 'Analytics',
        route: '/dashboard/case-analytics',
        navItem: NavItem.CaseAnalytics,
        navItemRedesign: NavItem.CaseAnalytics,
      },
      {
        id: 'case-templates',
        label: 'Templates',
        route: '/dashboard/case-templates',
        navItem: NavItem.CaseTemplate,
        navItemRedesign: NavItem.CaseTemplate,
      },
      {
        id: 'case-assignment-policies',
        label: 'Assignment Policies',
        route: '/dashboard/case-assignment-policies',
        navItem: NavItem.CaseAssignmentPolicies,
        navItemRedesign: NavItem.CaseAssignmentPolicies,
      },
      {
        id: 'case-actions',
        label: 'Actions',
        route: '/dashboard/case-actions',
        navItem: NavItem.CaseAction,
        navItemRedesign: NavItem.CaseAction,
      },
      {
        id: 'sar',
        label: 'SAR Filings',
        route: '/dashboard/sars',
        navItem: NavItem.CaseSAR,
        navItemRedesign: NavItem.CaseSAR,
        hidden: productFeatureDisabled(
          organization,
          ProductFeatureName.SARsDashboard,
          currentEnvironment?.type,
        ),
      },
    ],
  };

  const workflowData: NavbarItemData = {
    id: 'workflows',
    label: 'Workflows',
    navItem: NavItem.WorkflowHeader,
    navItemRedesign: NavItem.WorkflowHeader,
    prefixIcon: <WorkflowsIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'workflows-all',
        label: 'All Workflows',
        route: '/dashboard/workflows',
        navItem: NavItem.Workflow,
        navItemRedesign: NavItem.Workflow,
      },
      {
        id: 'workflow-runs',
        label: 'Workflow Runs',
        route: '/dashboard/workflow-runs',
        navItem: NavItem.WorkflowRuns,
        navItemRedesign: NavItem.WorkflowRuns,
      },
    ],
  };

  const exporterData: NavbarItemData = {
    id: 'exporters',
    label: 'Exports',
    navItem: NavItem.ExporterHeader,
    navItemRedesign: NavItem.ExporterHeader,
    prefixIcon: <DatabaseIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'exporters-all',
        label: 'All Exporters',
        route: '/dashboard/exporters',
        navItem: NavItem.Exporter,
        navItemRedesign: NavItem.Exporter,
      },
    ],
  };

  const dataSectionData: NavbarItemData = {
    id: 'data',
    label: 'Data',
    navItem: NavItem.DataHeader,
    navItemRedesign: NavItem.DataHeader,
    prefixIcon: <DatabaseIcon />,
    hidden: !useFeatureFlag('dashboard:enable-insight-queries-beta'),
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'insight-query',
        label: 'Insight Queries',
        route: '/dashboard/insight-queries',
        navItem: NavItem.InsightQuery,
        navItemRedesign: NavItem.InsightQuery,
      },
      {
        id: 'insight-query-tool',
        label: 'Insight Query Editor',
        route: '/dashboard/insight-query-tool',
        navItem: NavItem.InsightQueryTool,
        navItemRedesign: NavItem.InsightQueryTool,
      },
    ],
  };

  const importerData: NavbarItemData = {
    id: 'importers',
    label: 'Imports',
    navItem: NavItem.ImporterHeader,
    navItemRedesign: NavItem.ImporterHeader,
    prefixIcon: <DatabaseIcon />,
    disabled: currentEnvironment == null,
    hidden: productFeatureDisabled(
      organization,
      ProductFeatureName.Importers,
      currentEnvironment?.type,
    ),
    links: [
      {
        id: 'importers-all',
        label: 'All Importers',
        route: '/dashboard/imports',
        navItem: NavItem.Importer,
        navItemRedesign: NavItem.Importer,
      },
    ],
  };

  const integrationsData: NavbarItemData = {
    id: 'integrations',
    label: 'Integrations',
    navItem: NavItem.IntegrationsHeader,
    navItemRedesign: NavItem.IntegrationsHeader,
    prefixIcon: <GridIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'marketplace-all',
        label: 'Marketplace',
        route: '/dashboard/marketplace',
        navItem: NavItem.Integrations,
        navItemRedesign: NavItem.Integrations,
      },
      {
        id: 'integrations-all',
        label: 'Integrations',
        route: '/dashboard/integrations',
        navItem: NavItem.Integrations,
        navItemRedesign: NavItem.Integrations,
        hidden: productFeatureDisabled(
          organization,
          ProductFeatureName.WorkflowsEnhanced,
          currentEnvironment?.type,
        ),
      },
      {
        id: 'integrations-logs',
        label: 'Integration Logs',
        route: '/dashboard/integration-logs',
        navItem: NavItem.IntegrationLogs,
        navItemRedesign: NavItem.IntegrationLogs,
        hidden: productFeatureDisabled(
          organization,
          ProductFeatureName.WorkflowsEnhanced,
          currentEnvironment?.type,
        ),
      },
    ],
  };

  const listData: NavbarItemData = {
    id: 'lists',
    label: 'Lists',
    navItem: NavItem.ListHeader,
    navItemRedesign: NavItem.ListHeader,
    prefixIcon: <ListIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'lists-all',
        label: 'All Lists',
        route: '/dashboard/lists',
        navItem: NavItem.Lists,
        navItemRedesign: NavItem.Lists,
      },
    ],
  };

  let documentationUrl;
  const DOCS_HOST = 'https://docs.withpersona.com';
  if (!readmeJwtToken) {
    // Fallback to docs without being logged in
    documentationUrl = `${DOCS_HOST}/v${LATEST_API_VERSION}/docs`;
  } else {
    // NOTE: we assume latest version. They may have multiple API keys using diff versions
    documentationUrl = `${DOCS_HOST}/v${LATEST_API_VERSION}/docs?auth_token=${readmeJwtToken}`;
  }

  const apiData: NavbarItemData = {
    id: 'api',
    label: 'API',
    navItem: NavItem.ApiHeader,
    navItemRedesign: NavItem.ApiHeader,
    prefixIcon: <CodeIcon />,
    disabled: currentEnvironment == null,
    links: [
      {
        id: 'api-keys',
        label: 'API Keys',
        route: '/dashboard/api-keys',
        navItem: NavItem.ApiKey,
        navItemRedesign: NavItem.ApiKey,
      },
      {
        id: 'api-logs',
        label: 'API Logs',
        route: '/dashboard/api-logs',
        navItem: NavItem.ApiLogs,
        navItemRedesign: NavItem.ApiLogs,
      },
      {
        id: 'api-analytics',
        label: 'Analytics',
        route: '/dashboard/api-analytics',
        navItem: NavItem.ApiAnalytics,
        navItemRedesign: NavItem.ApiAnalytics,
      },
      {
        id: 'api-rate-limits',
        label: 'Rate Limits',
        route: '/dashboard/api-rate-limits',
        navItem: NavItem.RateLimits,
        navItemRedesign: NavItem.RateLimits,
      },
      {
        id: 'api-reference',
        label: (
          <DocumentationLink>
            Reference
            <LinkExternalIcon />
          </DocumentationLink>
        ),
        labelText: 'Reference',
        route: 'https://docs.withpersona.com/reference/introduction',
        navItem: NavItem.Reference,
        navItemRedesign: NavItem.Reference,
      },
    ],
  };

  const webhooksData: NavbarItemData = {
    id: 'webhooks',
    label: 'Webhooks',
    navItem: NavItem.WebhookHeader,
    navItemRedesign: NavItem.WebhookHeader,
    prefixIcon: <WebhooksIcon />,
    links: [
      {
        id: 'webhooks-all',
        label: 'Webhooks',
        route: '/dashboard/webhooks',
        navItem: NavItem.Webhook,
        navItemRedesign: NavItem.Webhook,
        disabled: currentEnvironment == null,
      },
      {
        id: 'webhook-events',
        label: 'Webhook Events',
        route: '/dashboard/webhook-events',
        navItem: NavItem.WebhookEvent,
        navItemRedesign: NavItem.WebhookEvent,
        disabled: currentEnvironment == null,
      },
    ],
  };

  const eventsData: NavbarItemData = {
    id: 'Events',
    label: 'Events',
    navItem: NavItem.EventsHeader,
    navItemRedesign: NavItem.EventsHeader,
    prefixIcon: <ZapIcon />,
    links: [
      {
        id: 'events-all',
        label: 'Events',
        route: '/dashboard/events',
        navItem: NavItem.Events,
        navItemRedesign: NavItem.Events,
        disabled: currentEnvironment == null,
      },
    ],
  };

  const teamData: NavbarItemData = {
    id: 'team',
    label: 'Team',
    navItem: NavItem.TeamHeader,
    navItemRedesign: NavItem.TeamHeader,
    prefixIcon: <UsersIcon />,
    links: [
      {
        id: 'users',
        label: 'Users',
        route: '/dashboard/users',
        navItem: NavItem.Team,
        navItemRedesign: NavItem.Users,
      },
      {
        id: 'roles',
        label: 'Roles',
        route: '/dashboard/user-roles',
        navItem: NavItem.Team,
        navItemRedesign: NavItem.Roles,
      },
      {
        id: 'teams',
        label: 'Teams',
        route: '/dashboard/teams',
        navItem: NavItem.Team,
        navItemRedesign: NavItem.Teams,
      },
      {
        id: 'user-sessions',
        label: 'Sessions',
        route: '/dashboard/user-sessions',
        navItem: NavItem.Team,
        navItemRedesign: NavItem.UserSessions,
        disabled: !usePermission(Permission.UserSessionsAllUsersView),
      },
      {
        id: 'user-audit-logs',
        label: 'Audit Logs',
        route: '/dashboard/user-audit-logs',
        navItem: NavItem.Team,
        navItemRedesign: NavItem.UserAuditLogs,
        disabled: !usePermission(Permission.UserAuditLogsAllUsersView),
      },
    ],
  };

  const organizationData: NavbarItemData = {
    id: 'organization',
    label: 'Organization',
    navItem: NavItem.OrganizationHeader,
    navItemRedesign: NavItem.OrganizationHeader,
    prefixIcon: <GlobeIcon />,
    links: [
      {
        id: 'information',
        label: 'Information',
        route: '/dashboard/organization',
        navItem: NavItem.Organization,
        navItemRedesign: NavItem.Organization,
      },
      {
        id: 'security',
        label: 'Security',
        route: '/dashboard/security',
        navItem: NavItem.Organization,
        navItemRedesign: NavItem.Security,
      },
      {
        id: 'billing',
        label: 'Billing',
        route: '/dashboard/billing',
        navItem: NavItem.Organization,
        navItemRedesign: NavItem.Billing,
        disabled: disableBillingTab,
      },
      {
        id: 'email-addresses',
        label: 'Email addresses',
        route: '/dashboard/email-addresses',
        navItem: NavItem.Organization,
        navItemRedesign: NavItem.Organization,
        hidden: !useFeatureFlag('dashboard:persona-hosted-email-addresses'),
      },
    ],
  };

  const resourcesData: NavbarItemData = {
    id: 'documentation',
    label: 'Documentation',
    navItem: NavItem.DocumentationHeader,
    navItemRedesign: NavItem.DocumentationHeader,
    prefixIcon: <BookOpenIcon />,
    links: [
      {
        id: 'getting-started',
        label: 'Getting Started',
        route: '/dashboard/getting-started',
        navItem: NavItem.Guides,
        navItemRedesign: NavItem.Guides,
      },
      {
        id: 'api-reference',
        label: (
          <DocumentationLink>
            API Reference
            <LinkExternalIcon />
          </DocumentationLink>
        ),
        labelText: 'API Reference',
        route: documentationUrl,
        navItem: NavItem.Development,
        navItemRedesign: NavItem.Development,
      },
      {
        id: 'help-center',
        label: (
          <DocumentationLink>
            Help Center
            <LinkExternalIcon />
          </DocumentationLink>
        ),
        labelText: 'Help Center',
        route: 'https://help.withpersona.com/',
        navItem: NavItem.HelpCenter,
        navItemRedesign: NavItem.HelpCenter,
      },
      {
        id: 'system-status',
        label: (
          <DocumentationLink>
            System Status
            <LinkExternalIcon />
          </DocumentationLink>
        ),
        labelText: 'System Status',
        route: 'https://status.withpersona.com/',
        navItem: NavItem.SystemStatus,
        navItemRedesign: NavItem.SystemStatus,
      },
      {
        id: 'coverage-map',
        label: 'Coverage Map',
        route: '/dashboard/resources/coverage-map/',
        navItem: NavItem.CoverageMap,
        navItemRedesign: NavItem.CoverageMap,
        hidden: organization?.attributes?.status !== OrganizationStatus.Active,
      },
      {
        id: 'verification-checks',
        label: 'Verification Checks',
        route: '/dashboard/resources/verification-checks',
        navItem: NavItem.VerificationChecks,
        navItemRedesign: NavItem.VerificationChecks,
        hidden: organization?.attributes?.status !== OrganizationStatus.Active,
      },
    ],
  };

  return [
    {
      id: 'home',
      label: 'HOME',
      items: [homeData],
    },
    {
      id: 'identity',
      label: 'IDENTITY',
      items: [inquiryData, verificationData, accountData, transactionData, reportData],
    },
    {
      id: 'platform',
      label: 'PLATFORM',
      items: [
        caseData,
        workflowData,
        graphV2Data,
        exporterData,
        importerData,
        dataSectionData,
        listData,
        integrationsData,
      ],
    },
    {
      id: 'developers',
      label: 'DEVELOPERS',
      items: [apiData, webhooksData, eventsData],
    },
    {
      id: 'admin',
      label: 'ADMIN',
      items: [teamData, organizationData, resourcesData],
    },
  ];
};

export default useSidebarConfig;

const DocumentationLink = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  svg {
    transform: translate(0, 2px);
  }
`;
